import { t, type Locale } from '@sd/i18n';
import type { Command } from '@sd/sim';
import type { FleetCard, HexInfo } from '../map/describe';
import { escapeHtml, seatColor } from './format';
import { icon, type IconName } from './icons';
import { shipIcon } from './unitsBar';

/** An icon per order (#166): the compact card is a row of commands. */
const ORDER_ICONS: Readonly<Record<string, IconName>> = {
  stop: 'stop',
  home: 'home',
  scan: 'scan',
  outpost: 'build',
  raid: 'war',
  invade: 'flag',
  stance: 'shield',
  toCloud: 'cloud',
  toSite: 'build',
};

/**
 * Map-side panels (#55): the tooltip that follows the cursor and the card
 * of the selected object under the map, with its orders.
 */
export class Tooltip {
  private readonly el: HTMLElement;

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'sd-tooltip';
    this.el.hidden = true;
    parent.append(this.el);
  }

  show(info: HexInfo, x: number, y: number) {
    this.el.innerHTML = `<b>${escapeHtml(info.title)}</b>${info.lines
      .map((l) => `<span>${escapeHtml(l)}</span>`)
      .join('')}`;
    this.el.hidden = false;
    // Below-right of the cursor, flipped near the window's edges.
    const { innerWidth: w, innerHeight: h } = window;
    const box = this.el.getBoundingClientRect();
    const left = x + 18 + box.width > w ? x - 12 - box.width : x + 18;
    const top = y + 20 + box.height > h ? y - 12 - box.height : y + 20;
    this.el.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`;
  }

  hide() {
    this.el.hidden = true;
  }

  dispose() {
    this.el.remove();
  }
}

export type CardContent =
  | { readonly kind: 'fleet'; readonly owner: string; readonly card: FleetCard }
  | { readonly kind: 'hex'; readonly info: HexInfo };

export class SelectionCard {
  private readonly el: HTMLElement;
  private shown = '';
  /** A button held down is never redrawn under the finger: the click would be lost (#96). */
  private pressed = false;
  private pending: CardContent | null | undefined;

  constructor(
    parent: HTMLElement,
    private readonly locale: Locale,
    order: (command: Command) => void,
    /** ✕ on the card (#162): lets the pick go without a key or a right click. */
    close: () => void = () => {},
    /** The control scheme, for the line of how-to under the card (#162). */
    private readonly scheme: () => 'mouse' | 'touchpad' = () => 'mouse',
  ) {
    this.el = document.createElement('section');
    this.el.className = 'sd-panel sd-selection';
    this.el.hidden = true;
    parent.append(this.el);
    // The hint card stands above this one: it needs to know how tall it is.
    new ResizeObserver(() => {
      parent.style.setProperty('--selection-h', `${this.el.offsetHeight}px`);
    }).observe(this.el);
    this.el.addEventListener('pointerdown', () => (this.pressed = true));
    window.addEventListener('pointerup', () => {
      if (!this.pressed) return;
      this.pressed = false;
      // Let the click land first, then catch up.
      setTimeout(() => {
        if (this.pending !== undefined) this.show(this.pending);
      }, 0);
    });
    this.el.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button');
      if (button?.dataset.action === 'close') return close();
      const command = button?.dataset.command;
      if (command) order(JSON.parse(command) as Command);
    });
  }

  show(content: CardContent | null) {
    if (this.pressed) {
      this.pending = content;
      return;
    }
    this.pending = undefined;
    const html = content ? this.render(content) : '';
    if (html === this.shown) return;
    this.shown = html;
    this.el.innerHTML = html;
    this.el.hidden = !content;
  }

  private render(content: CardContent): string {
    if (content.kind === 'hex') {
      const { title, lines } = content.info;
      // A hex in three lines at most (#166); the rest on hover of its title.
      return `
        <h2 class="sd-selection__title" title="${escapeHtml(lines.join('\n'))}">${escapeHtml(title)}${this.closeButton()}</h2>
        ${lines
          .slice(0, 3)
          .map((l) => `<div class="sd-selection__meta">${escapeHtml(l)}</div>`)
          .join('')}`;
    }
    const { card, owner } = content;
    const buttons = card.actions
      .filter((a) => a.id !== 'move')
      .map((a) => {
        const command = a.command ? escapeHtml(JSON.stringify(a.command)) : '';
        const glyph = ORDER_ICONS[a.id];
        const button = `<button class="sd-btn sd-btn--small sd-order ${a.primary ? 'sd-btn--primary' : ''}" type="button" ${
          a.command ? `data-command="${command}"` : 'disabled'
        } title="${escapeHtml([a.label, a.reason ?? a.price].filter(Boolean).join(' — '))}">${glyph ? icon(glyph, 15) : ''}<span>${escapeHtml(a.label)}</span></button>`;
        // What it costs, or what stands in the way and how to clear it, under the button
        // itself (#150), not only on hover.
        const note = a.reason ?? a.price;
        return note
          ? `<span class="sd-card-action">${button}<small class="${a.reason ? 'sd-card-action__why' : ''}">${escapeHtml(note)}</small></span>`
          : button;
      })
      .join('');
    // The compact card (#166, the author's note: commands, not a sheet of text). What the
    // ship is for and how to drive it stays a hover away, on «i».
    const how = t(
      this.locale,
      this.scheme() === 'touchpad' ? 'ui.map.hintTouchpad' : 'ui.map.hint',
    );
    const notes = [...card.hints.filter((h) => h !== card.alert), how].join('\n\n');
    const hull = card.hull ?? 100;
    const progress = card.progress
      ? `<div class="sd-selection__progress sd-fill" style="--p:${Math.round(card.progress.value * 100)}%"><span>${escapeHtml(card.progress.label)} · ${Math.round(card.progress.value * 100)}%</span></div>`
      : '';
    return `
      <h2 class="sd-selection__title">
        <span class="sd-selection__icon" style="color:${seatColor(owner)}">${card.lead ? shipIcon(card.lead, 18) : ''}</span>
        <span class="sd-selection__name">${escapeHtml(card.title)}</span>
        <span class="sd-selection__hull ${hull < 50 ? 'sd-selection__hull--low' : ''}" title="${escapeHtml(card.meta)}"><i style="width:${hull}%"></i></span>
        <span class="sd-selection__status">${escapeHtml(card.status ?? card.meta)}</span>
        <span class="sd-selection__info" tabindex="0" role="note" aria-label="${escapeHtml(notes)}" title="${escapeHtml(notes)}">${icon('info', 15)}</span>
        ${this.closeButton()}
      </h2>
      ${card.alert ? `<p class="sd-selection__alert">${escapeHtml(card.alert)}</p>` : ''}
      ${progress}
      <div class="sd-selection__orders">${buttons}</div>`;
  }

  private closeButton(): string {
    const label = t(this.locale, 'ui.card.close');
    return `<button class="sd-btn sd-btn--icon sd-btn--small sd-selection__close" type="button" data-action="close" title="${label} (Esc)" aria-label="${label}">×</button>`;
  }

  dispose() {
    this.el.remove();
  }
}
